/**
 * 🎯 WellAssignmentManager Component
 * 
 * Tasks #84-88: The command center for well assignments.
 * 
 * This is the main two-column layout where managers:
 * - View and select pumpers with workload indicators
 * - View and select wells with assignment status
 * - Assign wells to pumpers (primary/backup)
 * - Create temporary coverage
 * - Manage orphan wells
 * - View assignment history
 * 
 * Layout sections:
 * 1. Orphan Wells Warning Banner (if any)
 * 2. Active Coverage Panel (collapsible)
 * 3. Workload Balance Summary
 * 4. Two-Column Selection: Pumpers | Wells
 * 5. Action Buttons Row
 * 6. Recent Assignment History
 * 
 * @module WellAssignmentManager
 * @version 1.0.0
 */

import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Skeleton } from '../ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Progress } from '../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import {
  AlertTriangle,
  Users,
  Map,
  Search,
  Filter,
  ChevronRight,
  ChevronDown,
  Clock,
  UserPlus,
  UserMinus,
  Route,
  Shield,
  Calendar,
  History,
  RefreshCw,
  MoreHorizontal,
  CheckCircle2,
  XCircle,
  Loader2,
  Settings,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../ui/utils';

// Dialog
import WellAssignmentDialog, { 
  type WellForAssignment as DialogWellForAssignment, 
  type PumperOption, 
  type AssignmentResult as DialogAssignmentResult 
} from './WellAssignmentDialog';

// Hooks
import { useWellAssignments, type WellAssignmentInfo } from '../../hooks/useWellAssignments';
import { useTemporaryAssignments, type CoverageInfo } from '../../hooks/useTemporaryAssignments';
import { useTeamWorkloadDashboard } from '../../hooks/usePumperWorkload';
import { useOrphanWells, useOrphanWellsBanner } from '../../hooks/useOrphanWells';
import { useAuth } from '../../hooks/useAuth';

// Utils
import { getCompanyId } from '../../utils/tenantContext';

// Types
import type { WorkloadStatus } from '../../types/wellAssignment';

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

interface PumperListItem {
  id: string;
  name: string;
  email?: string;
  wellCount: number;
  workloadScore: number;
  workloadStatus: WorkloadStatus;
  isSelected: boolean;
  hasCoverage: boolean; // Providing or receiving coverage
}

interface WellListItem {
  id: string;
  name: string;
  status: string;
  primaryPumperId?: string;
  primaryPumperName?: string;
  backupPumperId?: string;
  backupPumperName?: string;
  isOrphan: boolean;
  hasCoverage: boolean;
  isSelected: boolean;
}

type WellFilterType = 'all' | 'assigned' | 'unassigned' | 'has-backup' | 'no-backup';

interface WellAssignmentManagerProps {
  /** Company ID (optional, uses context if not provided) */
  companyId?: string;
  /** Callback when assignment changes */
  onAssignmentChange?: () => void;
  /** Show compact view */
  compact?: boolean;
  /** Dark mode flag */
  isDarkMode?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

/** Text color classes for workload status indicators */
export const WORKLOAD_COLORS: Record<WorkloadStatus, string> = {
  light: 'text-green-600 dark:text-green-400',
  balanced: 'text-yellow-600 dark:text-yellow-400',
  heavy: 'text-orange-600 dark:text-orange-400',
  overloaded: 'text-red-600 dark:text-red-400',
};

const WORKLOAD_BG_COLORS: Record<WorkloadStatus, string> = {
  light: 'bg-green-500',
  balanced: 'bg-yellow-500',
  heavy: 'bg-orange-500',
  overloaded: 'bg-red-500',
};

const WORKLOAD_ICONS: Record<WorkloadStatus, string> = {
  light: '🟢',
  balanced: '🟡',
  heavy: '🟠',
  overloaded: '🔴',
};

const WORKLOAD_LABELS: Record<WorkloadStatus, string> = {
  light: 'Light',
  balanced: 'Balanced',
  heavy: 'Heavy',
  overloaded: 'Overloaded',
};

// ═══════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Orphan Wells Warning Banner
 */
function OrphanWellsBanner({ 
  onAssignClick,
  isDarkMode = false
}: { 
  onAssignClick: () => void;
  isDarkMode?: boolean;
}) {
  const banner = useOrphanWellsBanner();
  
  if (!banner.show) return null;
  
  const severityGradients = {
    new: 'from-yellow-500 to-yellow-600',
    warning: 'from-orange-500 to-red-500',
    critical: 'from-red-500 to-rose-500',
    emergency: 'from-red-600 to-rose-600',
    none: 'from-gray-500 to-slate-500',
  };
  
  const severityBgGradients = {
    new: 'from-yellow-400/20 to-yellow-500/20',
    warning: 'from-orange-400/20 to-red-400/20',
    critical: 'from-red-400/20 to-rose-400/20',
    emergency: 'from-red-500/30 to-rose-500/30',
    none: 'from-gray-400/20 to-slate-400/20',
  };
  
  return (
    <div className={cn(
      'relative overflow-hidden rounded-3xl',
      banner.severity === 'emergency' ? 'animate-pulse' : ''
    )}>
      {/* Gradient border layer */}
      <div className={cn(
        'absolute inset-0 rounded-3xl opacity-100',
        'bg-gradient-to-br',
        severityGradients[banner.severity]
      )} />
      <Card className={cn(
        'relative border-0 m-[2px]',
        isDarkMode ? 'bg-slate-900/95' : 'bg-transparent'
      )}>
        <CardContent className={cn(
          'py-4 rounded-[1.3rem]',
          'bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl'
        )}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{banner.icon}</span>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Unassigned Wells ({banner.count})
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {banner.message}
                </p>
              </div>
            </div>
            <Button 
              type="button"
              onClick={onAssignClick}
              variant={banner.severity === 'emergency' ? 'destructive' : 'default'}
              className="shrink-0"
            >
              {banner.actionLabel}
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Active Coverage Summary Card
 */
function ActiveCoverageCard({
  coverages,
  loading,
  onViewAll,
  onExtend,
  onEnd,
  onMakePermanent,
  isDarkMode = false,
}: {
  coverages: CoverageInfo[];
  loading: boolean;
  onViewAll: () => void;
  onExtend: (coverage: CoverageInfo) => void;
  onEnd: (coverage: CoverageInfo) => void;
  onMakePermanent: (coverage: CoverageInfo) => void;
  isDarkMode?: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  
  if (loading) {
    return (
      <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }
  
  if (coverages.length === 0) return null;
  
  return (
    <Card className="overflow-hidden bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
      <CardHeader className={cn(
        'pb-3',
        'bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-500 dark:to-cyan-500'
      )}>
        <div className="flex items-center justify-between">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-white hover:text-white/90 transition-colors"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-white" />
            ) : (
              <ChevronRight className="h-4 w-4 text-white" />
            )}\n            <Clock className="h-4 w-4 text-white" />
            <CardTitle className="text-base text-white">
              Active Coverage ({coverages.length})
            </CardTitle>
          </button>
          <Button variant="ghost" size="sm" onClick={onViewAll} className="text-white hover:bg-white/20 hover:text-white">
            View All
            <ChevronRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className={cn(
          "pt-0",
          isDarkMode ? "bg-slate-900" : "bg-white"
        )}>
          <div className="space-y-3">
            {coverages.slice(0, 3).map((coverage) => (
              <div
                key={coverage.id}
                className={cn(
                  'p-3 rounded-lg border',
                  isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      'font-medium truncate',
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    )}>
                      {coverage.coveringPumperName} covering for {coverage.originalPumperName}
                    </p>
                    <p className={cn(
                      'text-sm',
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    )}>
                      {coverage.wellCount} wells · {coverage.daysRemaining !== null 
                        ? `${coverage.daysRemaining} days remaining`
                        : 'Review pending'
                      }
                    </p>
                    {coverage.daysRemaining !== null && coverage.daysRemaining <= 3 && (
                      <div className="mt-2">
                        <Progress 
                          value={coverage.progressPercent} 
                          className="h-1.5"
                        />
                      </div>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className={cn(
                        "h-8 w-8",
                        isDarkMode ? "hover:bg-slate-700" : "hover:bg-gray-100"
                      )}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700">
                      <DropdownMenuItem onClick={() => onExtend(coverage)}>
                        <Calendar className="mr-2 h-4 w-4" />
                        Extend
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onEnd(coverage)}>
                        <XCircle className="mr-2 h-4 w-4" />
                        End Now
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-gray-200 dark:bg-slate-700" />
                      <DropdownMenuItem onClick={() => onMakePermanent(coverage)}>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Make Permanent
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
            {coverages.length > 3 && (
              <p className="text-sm text-muted-foreground text-center">
                +{coverages.length - 3} more coverages
              </p>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

/**
 * Workload Balance Summary
 */
function WorkloadBalanceSummary({
  onRebalance,
  summary,
  comparison,
  loading,
  isDarkMode = false,
}: {
  onRebalance: () => void;
  summary: any;
  comparison: any[];
  loading: boolean;
  isDarkMode?: boolean;
}) {
  if (loading) {
    return (
      <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-8 w-24" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Get top 4 pumpers by workload score (highest first)
  // Defensive: ensure comparison is an array before spreading
  const topPumpers = Array.isArray(comparison) 
    ? [...comparison]
        .sort((a, b) => (b.workloadScore || 0) - (a.workloadScore || 0))
        .slice(0, 4)
    : [];
  
  const balanceIndex = summary?.healthScore ?? 0;
  
  return (
    <Card className="overflow-hidden bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
      <CardHeader className={cn(
        'pb-3',
        'bg-gradient-to-r from-purple-600 to-violet-600 dark:from-purple-500 dark:to-violet-500'
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-white" />
            <CardTitle className="text-base text-white">Workload Balance</CardTitle>
            {balanceIndex > 0 && (
              <Badge variant="secondary" className="ml-2 bg-white/20 text-white border-white/30">
                {balanceIndex}% balanced
              </Badge>
            )}\n          </div>
          <Button variant="ghost" size="sm" onClick={onRebalance} className="text-white hover:bg-white/20 hover:text-white">
            Rebalance
            <ChevronRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className={cn(
        "pt-0",
        isDarkMode ? "bg-slate-900" : "bg-white"
      )}>
        <div className="flex flex-wrap gap-4">
          {topPumpers.map((pumper) => (
            <TooltipProvider key={pumper.pumperId}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "text-sm font-medium truncate max-w-[100px]",
                      isDarkMode ? "text-white" : "text-gray-900"
                    )}>
                      {pumper.pumperName?.split(' ')[0] || 'Unknown'}
                    </span>
                    <div className="flex items-center gap-1">
                      <div className={cn(
                        "w-20 h-2 rounded-full overflow-hidden",
                        isDarkMode ? "bg-slate-700" : "bg-gray-200"
                      )}>
                        <div
                          className={cn(
                            'h-full transition-all',
                            WORKLOAD_BG_COLORS[pumper.workloadStatus]
                          )}
                          style={{ width: `${Math.min(pumper.workloadScore || 0, 100)}%` }}
                        />
                      </div>
                      <span className={cn(
                        "text-xs",
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      )}>
                        {pumper.workloadScore || 0}
                      </span>
                      <span>{WORKLOAD_ICONS[pumper.workloadStatus]}</span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700">
                  <p className="text-gray-900 dark:text-white">{pumper.pumperName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {pumper.wellCount} wells · {WORKLOAD_LABELS[pumper.workloadStatus]}
                  </p>
                  {pumper.canTakeMore && (
                    <p className="text-xs text-green-500">Can take {pumper.capacityRemaining} more</p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Pumper Selection Panel
 */
function PumperPanel({
  pumpers,
  selectedPumperId,
  onSelectPumper,
  loading,
  searchQuery,
  onSearchChange,
  isDarkMode = false,
}: {
  pumpers: PumperListItem[];
  selectedPumperId: string | null;
  onSelectPumper: (pumperId: string | null) => void;
  loading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  isDarkMode?: boolean;
}) {
  // Defensive: ensure pumpers is an array
  const safePumpers = Array.isArray(pumpers) ? pumpers : [];
  
  const filteredPumpers = useMemo(() => {
    if (!searchQuery) return safePumpers;
    const q = searchQuery.toLowerCase();
    return safePumpers.filter(p => 
      p.name.toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q)
    );
  }, [safePumpers, searchQuery]);
  
  // Separate unassigned count
  const unassignedCount = safePumpers.reduce((acc, p) => acc + (p.wellCount === 0 ? 1 : 0), 0);
  
  return (
    <Card className="h-full flex flex-col overflow-hidden bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
      <CardHeader className={cn(
        'pb-3 shrink-0',
        'bg-gradient-to-r from-emerald-600 to-green-600 dark:from-emerald-500 dark:to-green-500'
      )}>
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-white" />
          <CardTitle className="text-base text-white">Pumpers</CardTitle>
        </div>
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search pumpers..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 bg-white dark:bg-slate-800 border-white/30"
          />
        </div>
      </CardHeader>
      
      <CardContent className={cn(
        "flex-1 overflow-hidden pt-0",
        isDarkMode ? "bg-slate-900" : "bg-white"
      )}>
        <ScrollArea className="h-full pr-4">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className={cn("h-14 w-full", isDarkMode && "bg-slate-800")} />
              ))}
            </div>
          ) : filteredPumpers.length === 0 ? (
            <div className={cn(
              "flex flex-col items-center justify-center py-8",
              isDarkMode ? "text-gray-400" : "text-gray-500"
            )}>
              <Users className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No pumpers found</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredPumpers.map((pumper) => (
                <button
                  key={pumper.id}
                  onClick={() => onSelectPumper(
                    selectedPumperId === pumper.id ? null : pumper.id
                  )}
                  className={cn(
                    'w-full p-3 rounded-lg text-left transition-colors',
                    isDarkMode 
                      ? 'hover:bg-slate-800 text-white' 
                      : 'hover:bg-gray-100 text-gray-900',
                    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    selectedPumperId === pumper.id && (isDarkMode 
                      ? 'bg-emerald-900/30 border border-emerald-500'
                      : 'bg-emerald-50 border border-emerald-500')
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={cn(
                        'w-2 h-2 rounded-full shrink-0',
                        selectedPumperId === pumper.id ? 'bg-emerald-500' : (isDarkMode ? 'bg-slate-600' : 'bg-gray-300')
                      )} />
                      <span className={cn("font-medium truncate", isDarkMode ? "text-white" : "text-gray-900")}>{pumper.name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="secondary" className="text-xs">
                        {pumper.wellCount}
                      </Badge>
                      <span>{WORKLOAD_ICONS[pumper.workloadStatus]}</span>
                    </div>
                  </div>
                  {pumper.hasCoverage && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                      <Clock className="h-3 w-3" />
                      <span>Active coverage</span>
                    </div>
                  )}
                </button>
              ))}
              
              {unassignedCount > 0 && (
                <>
                  <Separator className="my-2" />
                  <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 text-yellow-800 dark:text-yellow-200">
                    <div className="flex items-center gap-2 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      <span>{unassignedCount} pumpers with no wells</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

/**
 * Well Selection Panel
 */
function WellPanel({
  wells,
  selectedWellIds,
  onToggleWell,
  onSelectAll,
  onClearSelection,
  loading,
  searchQuery,
  onSearchChange,
  filter,
  onFilterChange,
  selectedPumperId,
  isDarkMode = false,
}: {
  wells: WellListItem[];
  selectedWellIds: Set<string>;
  onToggleWell: (wellId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  loading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filter: WellFilterType;
  onFilterChange: (filter: WellFilterType) => void;
  selectedPumperId: string | null;
  isDarkMode?: boolean;
}) {
  // Defensive: ensure wells is an array
  const safeWells = Array.isArray(wells) ? wells : [];
  
  const filteredWells = useMemo(() => {
    let result = safeWells;
    
    // Text search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(w => w.name.toLowerCase().includes(q));
    }
    
    // Status filter
    switch (filter) {
      case 'assigned':
        result = result.filter(w => !w.isOrphan);
        break;
      case 'unassigned':
        result = result.filter(w => w.isOrphan);
        break;
      case 'has-backup':
        result = result.filter(w => !!w.backupPumperId);
        break;
      case 'no-backup':
        result = result.filter(w => !w.backupPumperId && !w.isOrphan);
        break;
    }
    
    return result;
  }, [safeWells, searchQuery, filter]);
  
  const selectedCount = selectedWellIds.size;
  const allSelected = filteredWells.length > 0 && 
    filteredWells.every(w => selectedWellIds.has(w.id));
  
  return (
    <Card className="h-full flex flex-col overflow-hidden bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
      <CardHeader className={cn(
        'pb-3 shrink-0',
        'bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-500 dark:to-blue-500'
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Map className="h-4 w-4 text-white" />
            <CardTitle className="text-base text-white">Wells</CardTitle>
            {selectedCount > 0 && (
              <Badge variant="default" className="ml-1 bg-white/20 text-white border-white/30">
                {selectedCount} selected
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {selectedCount > 0 ? (
              <Button variant="ghost" size="sm" onClick={onClearSelection} className="text-white hover:bg-white/20 hover:text-white">
                Clear
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={onSelectAll} className="text-white hover:bg-white/20 hover:text-white">
                Select All
              </Button>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search wells..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9 bg-white dark:bg-slate-800 border-white/30"
            />
          </div>
          <Select value={filter} onValueChange={(v) => onFilterChange(v as WellFilterType)}>
            <SelectTrigger className="w-[140px] bg-white dark:bg-slate-800 border-white/30 text-white">
              <Filter className="h-4 w-4 mr-2 text-white" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Wells</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              <SelectItem value="has-backup">Has Backup</SelectItem>
              <SelectItem value="no-backup">No Backup</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent className={cn(
        "flex-1 overflow-hidden pt-0",
        isDarkMode ? "bg-slate-900" : "bg-white"
      )}>
        <ScrollArea className="h-full pr-4">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className={cn("h-16 w-full", isDarkMode && "bg-slate-800")} />
              ))}
            </div>
          ) : filteredWells.length === 0 ? (
            <div className={cn(
              "flex flex-col items-center justify-center py-8",
              isDarkMode ? "text-gray-400" : "text-gray-500"
            )}>
              <Map className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No wells found</p>
            </div>
          ) : (
            <div className="space-y-1">
              {/* Select all checkbox */}
              <div className={cn(
                "flex items-center gap-2 p-2 border-b",
                isDarkMode ? "border-slate-700" : "border-gray-200"
              )}>
                <Checkbox
                  id="select-all"
                  checked={allSelected}
                  onCheckedChange={(checked) => {
                    if (checked) onSelectAll();
                    else onClearSelection();
                  }}
                />
                <label 
                  htmlFor="select-all" 
                  className={cn(
                    "text-sm cursor-pointer",
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  )}
                >
                  Select all ({filteredWells.length})
                </label>
              </div>
              
              {filteredWells.map((well) => {
                const isAssignedToSelected = selectedPumperId && 
                  (well.primaryPumperId === selectedPumperId || 
                   well.backupPumperId === selectedPumperId);
                
                return (
                  <div
                    key={well.id}
                    className={cn(
                      'p-3 rounded-lg transition-colors',
                      isDarkMode 
                        ? 'hover:bg-slate-800' 
                        : 'hover:bg-gray-100',
                      selectedWellIds.has(well.id) && (isDarkMode 
                        ? 'bg-cyan-900/30 border border-cyan-500' 
                        : 'bg-cyan-50 border border-cyan-500'),
                      isAssignedToSelected && 'ring-2 ring-blue-500/50'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={`well-${well.id}`}
                        checked={selectedWellIds.has(well.id)}
                        onCheckedChange={() => onToggleWell(well.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <label 
                            htmlFor={`well-${well.id}`}
                            className={cn(
                              "font-medium cursor-pointer truncate",
                              isDarkMode ? "text-white" : "text-gray-900"
                            )}
                          >
                            {well.name}
                          </label>
                          {well.isOrphan && (
                            <Badge variant="destructive" className="text-xs">
                              Unassigned
                            </Badge>
                          )}
                          {well.hasCoverage && (
                            <Badge variant="secondary" className="text-xs">
                              Coverage
                            </Badge>
                          )}
                        </div>
                        <div className={cn(
                          "mt-1 text-sm",
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        )}>
                          {well.isOrphan ? (
                            <span className="text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Needs assignment
                            </span>
                          ) : (
                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                              <span>Primary: {well.primaryPumperName || 'None'}</span>
                              {well.backupPumperId && (
                                <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
                                  Backup: {well.backupPumperName}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

/**
 * Action Buttons Row
 */
function ActionButtonsRow({
  selectedPumperId,
  selectedWellIds,
  onAssignPrimary,
  onAssignBackup,
  onAssignByRoute,
  onCreateCoverage,
  onActivateBackups,
  onUnassign,
  loading,
}: {
  selectedPumperId: string | null;
  selectedWellIds: Set<string>;
  onAssignPrimary: () => void;
  onAssignBackup: () => void;
  onAssignByRoute: () => void;
  onCreateCoverage: () => void;
  onActivateBackups: () => void;
  onUnassign: () => void;
  loading: boolean;
}) {
  const hasSelection = selectedPumperId && selectedWellIds.size > 0;
  // Reserved for pumper-only actions (create coverage, activate backups without wells selected)
  const hasPumperOnly = selectedPumperId && selectedWellIds.size === 0;
  void hasPumperOnly; // Suppresses unused warning until pumper-only action buttons are wired
  
  return (
    <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
      <CardContent className="py-4 bg-white dark:bg-slate-900">
        <div className="flex flex-wrap gap-2">
          {/* Primary assignment actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                disabled={!hasSelection || loading}
                className="gap-2"
              >
                <UserPlus className="h-4 w-4" />
                Assign as Primary
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700">
              <DropdownMenuItem onClick={onAssignPrimary}>
                <UserPlus className="mr-2 h-4 w-4" />
                Assign {selectedWellIds.size} well{selectedWellIds.size !== 1 ? 's' : ''}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onAssignPrimary}>
                <UserPlus className="mr-2 h-4 w-4" />
                Assign with reason...
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            variant="outline"
            disabled={!hasSelection || loading}
            onClick={onAssignBackup}
            className="gap-2"
          >
            <Shield className="h-4 w-4" />
            Assign as Backup
          </Button>
          
          <Separator orientation="vertical" className="h-9" />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Route className="h-4 w-4" />
                Assign by Route
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={onAssignByRoute}>
                <Route className="mr-2 h-4 w-4" />
                Select route...
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>
                <span className="text-muted-foreground text-xs">
                  Routes with wells: 5
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Separator orientation="vertical" className="h-9" />
          
          <Button
            variant="outline"
            disabled={!selectedPumperId || loading}
            onClick={onCreateCoverage}
            className="gap-2"
          >
            <Clock className="h-4 w-4" />
            <span className="hidden lg:inline">Create Temporary Coverage</span>
            <span className="lg:hidden">Temp Coverage</span>
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Shield className="h-4 w-4" />
                <span className="hidden lg:inline">Activate Backups for Pumper</span>
                <span className="lg:hidden">Activate Backups</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem 
                onClick={onActivateBackups}
                disabled={!selectedPumperId}
              >
                <Shield className="mr-2 h-4 w-4" />
                For selected pumper
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onActivateBackups}>
                <Shield className="mr-2 h-4 w-4" />
                Select pumper...
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {hasSelection && (
            <>
              <Separator orientation="vertical" className="h-9" />
              <Button
                variant="ghost"
                onClick={onUnassign}
                className="gap-2 text-destructive hover:text-destructive"
              >
                <UserMinus className="h-4 w-4" />
                Unassign
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Recent Assignment History
 */
function RecentHistoryCard({
  onViewAll,
  history,
  loading,
}: {
  onViewAll: () => void;
  history: Array<{
    id: string;
    action: string;
    pumperName?: string;
    wellName?: string;
    method?: string;
    timeAgo?: string;
    timestamp?: Date;
  }>;
  loading: boolean;
}) {
  // Get last 4 entries (defensive check)
  const recentEntries = Array.isArray(history) ? history.slice(0, 4) : [];
  
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'assigned':
        return <UserPlus className="h-3 w-3 text-green-500" />;
      case 'unassigned':
        return <UserMinus className="h-3 w-3 text-red-500" />;
      case 'backup_assigned':
        return <Shield className="h-3 w-3 text-blue-500" />;
      case 'coverage_started':
        return <Clock className="h-3 w-3 text-purple-500" />;
      case 'coverage_ended':
        return <CheckCircle2 className="h-3 w-3 text-green-500" />;
      default:
        return <History className="h-3 w-3 text-muted-foreground" />;
    }
  };
  
  return (
    <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
      <CardHeader className="pb-3 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <CardTitle className="text-base text-gray-900 dark:text-white">Recent Assignment History</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={onViewAll} className="text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800">
            View All
            <ChevronRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 bg-white dark:bg-slate-900">
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-6 w-full dark:bg-slate-800" />
            ))}
          </div>
        ) : recentEntries.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
            No recent assignment changes
          </p>
        ) : (
          <div className="space-y-2">
            {recentEntries.map((entry: typeof history[number], index: number) => (
              <div 
                key={entry.id || index}
                className="flex items-center gap-2 text-sm"
              >
                {getActionIcon(entry.action)}
                <span className="text-gray-400 dark:text-gray-500">•</span>
                <span className="flex-1 truncate">
                  {entry.pumperName && (
                    <span className="font-medium text-gray-900 dark:text-white">{entry.pumperName}</span>
                  )}
                  {' '}
                  <span className="text-gray-500 dark:text-gray-400">
                    {entry.action === 'assigned' ? 'assigned to' : entry.action.replace('_', ' ')}
                  </span>
                  {' '}
                  <span className="text-gray-900 dark:text-white">{entry.wellName}</span>
                  {entry.method && entry.method !== 'manual' && (
                    <span className="text-gray-500 dark:text-gray-400"> ({entry.method})</span>
                  )}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                  {entry.timeAgo || 'just now'}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function WellAssignmentManager({
  onAssignmentChange, // Called after assignment operations complete
  compact = false,
  isDarkMode = false,
}: WellAssignmentManagerProps) {
  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  
  const [selectedPumperId, setSelectedPumperId] = useState<string | null>(null);
  const [selectedWellIds, setSelectedWellIds] = useState<Set<string>>(new Set());
  const [pumperSearch, setPumperSearch] = useState('');
  const [wellSearch, setWellSearch] = useState('');
  const [wellFilter, setWellFilter] = useState<WellFilterType>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [visibilityMode, setVisibilityMode] = useState<'all' | 'assigned'>('all');
  
  // Dialog state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignDialogMode, setAssignDialogMode] = useState<'primary' | 'backup'>('primary');
  const [assignDialogLoading, setAssignDialogLoading] = useState(false);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // HOOKS
  // ─────────────────────────────────────────────────────────────────────────────
  
  // Get current user from auth context
  const { user } = useAuth();
  
  // Memoize authUser to prevent infinite loops in downstream hooks
  // Without useMemo, a new object is created every render, triggering useEffect re-runs
  const authUser = useMemo(() => {
    if (!user) return null;
    return { id: user.id, role: user.role || 'viewer', companyId: user.companyId };
  }, [user?.id, user?.role, user?.companyId]);
  
  const {
    wells: assignments,
    history,
    loading: assignmentsLoading,
    refresh: refreshAssignments,
    assignWell,
    bulkAssign,
    unassignWell,
  } = useWellAssignments({ user: authUser });
  
  const {
    coverages: activeCoverages,
    loading: coveragesLoading,
  } = useTemporaryAssignments({ user: authUser });
  
  const {
    orphanWells,
    loading: orphansLoading,
  } = useOrphanWells(); // Uses tenant context internally

  // Get team workload data (includes ALL pumpers, even those with 0 wells)
  const { 
    summary: workloadSummary, 
    comparison: workloadComparison, 
    loading: workloadLoading 
  } = useTeamWorkloadDashboard(authUser);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // DERIVED DATA
  // ─────────────────────────────────────────────────────────────────────────────
  
  // Transform workload comparison into pumper list
  // This ensures we show ALL pumpers, not just those with assignments
  const pumpers = useMemo((): PumperListItem[] => {
    // Defensive: ensure comparison is an array
    if (!Array.isArray(workloadComparison)) return [];
    
    return workloadComparison.map(pumper => {
      // Check if this pumper is involved in any active coverage
      let hasCoverage = false;
      if (Array.isArray(activeCoverages)) {
        hasCoverage = activeCoverages.some(c => 
          c.coveringPumperId === pumper.pumperId || 
          c.originalPumperId === pumper.pumperId
        );
      }

      return {
        id: pumper.pumperId,
        name: pumper.pumperName || 'Unknown',
        wellCount: pumper.wellCount || 0,
        workloadScore: pumper.workloadScore || 0,
        workloadStatus: pumper.workloadStatus || 'balanced',
        isSelected: pumper.pumperId === selectedPumperId,
        hasCoverage,
      };
    }).sort((a, b) => b.wellCount - a.wellCount);
  }, [workloadComparison, activeCoverages, selectedPumperId]);
  
  // Transform assignments into well list with full status info
  const wells = useMemo((): WellListItem[] => {
    // Defensive: ensure assignments is an array
    if (!Array.isArray(assignments)) return [];
    
    const wellList: WellListItem[] = assignments.map((assignment: WellAssignmentInfo) => ({
      id: assignment.id,
      name: assignment.name,
      status: assignment.status || 'active',
      primaryPumperId: assignment.primaryPumperId ?? undefined,
      primaryPumperName: assignment.primaryPumperName ?? undefined,
      backupPumperId: assignment.backupPumperId ?? undefined,
      backupPumperName: assignment.backupPumperName ?? undefined,
      isOrphan: !assignment.primaryPumperId && (assignment.assignedPumperIds?.length ?? 0) === 0,
      hasCoverage: assignment.temporaryAssignment?.active ?? false,
      isSelected: selectedWellIds.has(assignment.id),
    }));
    
    // Add orphan wells (defensive check)
    if (Array.isArray(orphanWells)) {
      orphanWells.forEach((orphan) => {
      if (!wellList.find(w => w.id === orphan.id)) {
        wellList.push({
          id: orphan.id,
          name: orphan.name,
          status: orphan.status,
          primaryPumperId: undefined,
          primaryPumperName: undefined,
          backupPumperId: undefined,
          backupPumperName: undefined,
          isOrphan: true,
          hasCoverage: false,
          isSelected: selectedWellIds.has(orphan.id),
        });
      }
    });
    }
    
    return wellList.sort((a, b) => {
      // Orphans first, then by name
      if (a.isOrphan && !b.isOrphan) return -1;
      if (!a.isOrphan && b.isOrphan) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [assignments, orphanWells, selectedWellIds]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshAssignments();
      toast.success('Data refreshed');
    } catch {
      toast.error('Failed to refresh');
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshAssignments]);
  
  const handleToggleWell = useCallback((wellId: string) => {
    setSelectedWellIds(prev => {
      const next = new Set(prev);
      if (next.has(wellId)) {
        next.delete(wellId);
      } else {
        next.add(wellId);
      }
      return next;
    });
  }, []);
  
  const handleSelectAllWells = useCallback(() => {
    const visibleWellIds = wells
      .filter(w => {
        if (wellSearch) {
          return w.name.toLowerCase().includes(wellSearch.toLowerCase());
        }
        return true;
      })
      .filter(w => {
        switch (wellFilter) {
          case 'assigned': return !w.isOrphan;
          case 'unassigned': return w.isOrphan;
          case 'has-backup': return !!w.backupPumperId;
          case 'no-backup': return !w.backupPumperId && !w.isOrphan;
          default: return true;
        }
      })
      .map(w => w.id);
    setSelectedWellIds(new Set(visibleWellIds));
  }, [wells, wellSearch, wellFilter]);
  
  const handleClearSelection = useCallback(() => {
    setSelectedWellIds(new Set());
  }, []);
  
  const handleAssignOrphans = useCallback(() => {
    // Select all orphan wells and open the assignment dialog
    const orphanIds = wells.filter(w => w.isOrphan).map(w => w.id);
    if (orphanIds.length === 0) {
      toast.info('No unassigned wells to assign');
      return;
    }
    setSelectedWellIds(new Set(orphanIds));
    setWellFilter('unassigned');
    setAssignDialogMode('primary');
    setShowAssignDialog(true);
    toast.info(`Selected ${orphanIds.length} unassigned wells for assignment`);
  }, [wells]);
  
  // Open assignment dialog for primary pumper
  const handleAssignPrimary = useCallback(() => {
    if (selectedWellIds.size === 0) {
      toast.warning('Please select at least one well to assign');
      return;
    }
    setAssignDialogMode('primary');
    setShowAssignDialog(true);
  }, [selectedWellIds.size]);
  
  // Open assignment dialog for backup pumper
  const handleAssignBackup = useCallback(() => {
    if (selectedWellIds.size === 0) {
      toast.warning('Please select at least one well to assign a backup');
      return;
    }
    setAssignDialogMode('backup');
    setShowAssignDialog(true);
  }, [selectedWellIds.size]);
  
  // Handle dialog submission - perform the actual assignment
  const handleAssignmentSubmit = useCallback(async (result: DialogAssignmentResult) => {
    if (!user) {
      toast.error('You must be logged in to make assignments');
      return;
    }
    
    setAssignDialogLoading(true);
    
    try {
      // Get full assignment data from assignments array (not simplified wells)
      const wellsToAssign = assignments.filter(a => selectedWellIds.has(a.id));
      
      if (wellsToAssign.length === 0) {
        toast.error('No wells selected');
        return;
      }
      
      // Build pumper object for assignment
      const pumper = { id: result.pumperId, name: result.pumperName };
      const assignOptions = {
        assignmentType: result.assignmentType,
        reason: result.reason,
        performedBy: user.id,
        performedByName: user.name || user.email || 'Manager',
      };
      
      if (wellsToAssign.length === 1) {
        // Single assignment
        const well = wellsToAssign[0];
        const assignResult = await assignWell(
          well.id,
          well.name,
          pumper,
          assignOptions
        );
        
        if (assignResult.success) {
          toast.success(`${well.name} assigned to ${result.pumperName}`);
        } else {
          toast.error(assignResult.error || 'Assignment failed');
        }
      } else {
        // Bulk assignment - use full WellAssignmentInfo data
        const wellsForBulk = wellsToAssign.map(w => ({
          id: w.id,
          name: w.name,
          companyId: user.companyId || getCompanyId(),
          assignedPumperIds: w.assignedPumperIds || [],
          status: w.status || 'active',
          primaryPumperId: w.primaryPumperId || undefined,
          primaryPumperName: w.primaryPumperName || undefined,
          backupPumperId: w.backupPumperId || undefined,
          backupPumperName: w.backupPumperName || undefined,
        }));
        
        const bulkResult = await bulkAssign(
          wellsForBulk,
          pumper,
          assignOptions
        );
        
        if (bulkResult.success) {
          toast.success(`${bulkResult.successCount} wells assigned to ${result.pumperName}`);
          if (bulkResult.failureCount > 0) {
            toast.warning(`${bulkResult.failureCount} wells failed to assign`);
          }
        } else {
          toast.error(`Assignment failed: ${bulkResult.failureCount} errors`);
        }
      }
      
      // Clear selection and close dialog on success
      setSelectedWellIds(new Set());
      setShowAssignDialog(false);
      
      // Refresh data
      await refreshAssignments();
      
      // Notify parent
      onAssignmentChange?.();
      
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error('Failed to complete assignment');
    } finally {
      setAssignDialogLoading(false);
    }
  }, [user, assignments, selectedWellIds, assignWell, bulkAssign, refreshAssignments, onAssignmentChange]);

  const handleAssignByRoute = useCallback(() => {
    toast.info('Opening route assignment dialog...');
    // TODO: Open AssignByRouteDialog
  }, []);
  
  const handleCreateCoverage = useCallback(() => {
    toast.info('Opening coverage dialog...');
    // TODO: Open TemporaryAssignmentDialog
  }, []);
  
  const handleActivateBackups = useCallback(() => {
    toast.info('Opening backup activation dialog...');
    // TODO: Open ActivateBackupsDialog
  }, []);
  
  const handleUnassign = useCallback(async () => {
    if (selectedWellIds.size === 0) {
      toast.warning('Please select wells to unassign');
      return;
    }
    
    if (!user) {
      toast.error('You must be logged in to unassign wells');
      return;
    }
    
    const wellsToUnassign = wells.filter(w => selectedWellIds.has(w.id));
    
    // Confirm before unassigning
    const confirmMsg = wellsToUnassign.length === 1 
      ? `Unassign ${wellsToUnassign[0].name}?`
      : `Unassign ${wellsToUnassign.length} wells?`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const well of wellsToUnassign) {
        const result = await unassignWell(
          well.id,
          well.name,
          well.primaryPumperId || null,
          {
            reason: 'Unassigned by manager',
            performedBy: user.id,
            performedByName: user.name || user.email || 'Manager',
          }
        );
        
        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} well${successCount !== 1 ? 's' : ''} unassigned`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} well${failCount !== 1 ? 's' : ''} failed to unassign`);
      }
      
      // Clear selection and refresh
      setSelectedWellIds(new Set());
      await refreshAssignments();
      onAssignmentChange?.();
      
    } catch (error) {
      console.error('Unassign error:', error);
      toast.error('Failed to unassign wells');
    }
  }, [selectedWellIds, wells, user, unassignWell, refreshAssignments, onAssignmentChange]);
  
  const handleExtendCoverage = useCallback((coverage: CoverageInfo) => {
    toast.info(`Extending coverage for ${coverage.coveringPumperName}...`);
    // TODO: Open extension dialog
  }, []);
  
  const handleEndCoverage = useCallback((coverage: CoverageInfo) => {
    toast.info(`Ending coverage for ${coverage.coveringPumperName}...`);
    // TODO: Confirm and end
  }, []);
  
  const handleMakePermanent = useCallback((coverage: CoverageInfo) => {
    toast.info(`Making coverage permanent for ${coverage.coveringPumperName}...`);
    // TODO: Confirm and make permanent
  }, []);
  
  const handleRebalance = useCallback(() => {
    toast.info('Opening rebalance suggestions...');
    // TODO: Open WorkloadRebalanceDialog
  }, []);
  
  const handleViewAllCoverages = useCallback(() => {
    toast.info('Opening coverages panel...');
    // TODO: Navigate to full coverage management
  }, []);
  
  const handleViewAllHistory = useCallback(() => {
    toast.info('Opening history panel...');
    // TODO: Open WellAssignmentHistory
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ─────────────────────────────────────────────────────────────────────────────
  
  const isLoading = assignmentsLoading || coveragesLoading || orphansLoading || workloadLoading;
  
  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  
  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-2xl font-bold">Well Assignments</h1>
          <p className="text-sm text-muted-foreground">
            Manage pumper well assignments and coverage
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2 hidden sm:inline">Refresh</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4" />
            <span className="ml-2 hidden sm:inline">Settings</span>
          </Button>
        </div>
      </div>
      
      {/* Orphan Wells Banner */}
      <OrphanWellsBanner onAssignClick={handleAssignOrphans} isDarkMode={isDarkMode} />
      
      {/* Active Coverage */}
      <ActiveCoverageCard
        coverages={Array.isArray(activeCoverages) ? activeCoverages : []}
        loading={coveragesLoading}
        onViewAll={handleViewAllCoverages}
        onExtend={handleExtendCoverage}
        onEnd={handleEndCoverage}
        onMakePermanent={handleMakePermanent}
        isDarkMode={isDarkMode}
      />
      
      {/* Workload Balance */}
      <WorkloadBalanceSummary 
        onRebalance={handleRebalance} 
        summary={workloadSummary}
        comparison={workloadComparison}
        loading={workloadLoading}
        isDarkMode={isDarkMode}
      />
      
      {/* Two-Column Selection */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-0">
        <PumperPanel
          pumpers={pumpers}
          selectedPumperId={selectedPumperId}
          onSelectPumper={setSelectedPumperId}
          loading={isLoading}
          searchQuery={pumperSearch}
          onSearchChange={setPumperSearch}
          isDarkMode={isDarkMode}
        />
        <WellPanel
          wells={wells}
          selectedWellIds={selectedWellIds}
          onToggleWell={handleToggleWell}
          onSelectAll={handleSelectAllWells}
          onClearSelection={handleClearSelection}
          loading={isLoading}
          searchQuery={wellSearch}
          onSearchChange={setWellSearch}
          filter={wellFilter}
          onFilterChange={setWellFilter}
          selectedPumperId={selectedPumperId}
          isDarkMode={isDarkMode}
        />
      </div>
      
      {/* Action Buttons */}
      <ActionButtonsRow
        selectedPumperId={selectedPumperId}
        selectedWellIds={selectedWellIds}
        onAssignPrimary={handleAssignPrimary}
        onAssignBackup={handleAssignBackup}
        onAssignByRoute={handleAssignByRoute}
        onCreateCoverage={handleCreateCoverage}
        onActivateBackups={handleActivateBackups}
        onUnassign={handleUnassign}
        loading={isLoading}
      />
      
      {/* Recent History */}
      {!compact && (
        <RecentHistoryCard 
          onViewAll={handleViewAllHistory} 
          history={(Array.isArray(history) ? history : []).map(h => {
            // Look up well name from assignments
            const matchedWell = Array.isArray(assignments) ? assignments.find(a => a.id === h.wellId) : null;
            // Handle FirestoreTimestamp union type (Date | { seconds, nanoseconds })
            const ts = h.timestamp 
              ? (h.timestamp instanceof Date 
                  ? h.timestamp 
                  : new Date((h.timestamp as { seconds: number }).seconds * 1000))
              : null;
            return {
              id: h.id,
              action: String(h.action),
              pumperName: h.pumperNames?.[0] ?? undefined,
              wellName: matchedWell?.name ?? h.wellId,
              method: h.method ? String(h.method) : undefined,
              timeAgo: ts ? formatTimeAgo(ts) : 'just now',
              timestamp: ts ?? undefined,
            };
          })}
          loading={assignmentsLoading}
        />
      )}
      
      {/* Visibility Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assignment Settings</DialogTitle>
            <DialogDescription>
              Configure how pumpers see wells in their field view.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Visibility Mode */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Pumper Well Visibility</Label>
              <p className="text-xs text-muted-foreground">
                Control which wells pumpers can see in their field app
              </p>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                  <input
                    type="radio"
                    name="visibility"
                    checked={visibilityMode === 'all'}
                    onChange={() => setVisibilityMode('all')}
                    className="w-4 h-4 text-primary"
                  />
                  <div>
                    <p className="font-medium">All Wells</p>
                    <p className="text-xs text-muted-foreground">
                      Pumpers can see all company wells (current behavior)
                    </p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                  <input
                    type="radio"
                    name="visibility"
                    checked={visibilityMode === 'assigned'}
                    onChange={() => setVisibilityMode('assigned')}
                    className="w-4 h-4 text-primary"
                  />
                  <div>
                    <p className="font-medium">Assigned Only</p>
                    <p className="text-xs text-muted-foreground">
                      Pumpers only see wells assigned to them
                    </p>
                  </div>
                </label>
              </div>
            </div>
            
            {/* Info note */}
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                <strong>Note:</strong> Managers and admins always see all wells regardless of this setting.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              toast.success(`Visibility set to: ${visibilityMode === 'all' ? 'All Wells' : 'Assigned Only'}`);
              setShowSettings(false);
              // TODO: Call setWellVisibilitySettings(visibilityMode) from service
            }}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Well Assignment Dialog */}
      <WellAssignmentDialog
        open={showAssignDialog}
        onClose={() => setShowAssignDialog(false)}
        onSubmit={handleAssignmentSubmit}
        wells={wells.filter(w => selectedWellIds.has(w.id)).map(w => ({
          id: w.id,
          name: w.name,
          currentPumperId: w.primaryPumperId || undefined,
          currentPumperName: w.primaryPumperName || undefined,
        })) as DialogWellForAssignment[]}
        pumpers={pumpers.map(p => ({
          id: p.id,
          name: p.name,
          email: p.email,
          workloadScore: p.workloadScore,
          workloadStatus: p.workloadStatus,
          wellCount: p.wellCount,
        })) as PumperOption[]}
        loading={assignDialogLoading}
        defaultPumperId={selectedPumperId || undefined}
        defaultAssignmentType={assignDialogMode}
      />
    </div>
  );
}

/**
 * Format timestamp to relative time string
 */
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
